import { useState, useEffect } from "react";
import { expensesService } from "../services/api";
import {
  formatCurrency,
  toPersianNumbers,
  toEnglishNumbers,
} from "../utils/numbers";
import { Plus, Check, AlertCircle } from "lucide-react";
import { Modal } from "../components/Modal";
import JalaliDatePicker from "../components/JalaliDatePicker";

interface Expense {
  id: number;
  expense_type_name: string;
  amount: string | number;
  expense_date: string;
}

interface ExpenseType {
  id: number;
  name: string;
}

export default function Expenses() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    expense_type_id: "",
    amount: "",
    expense_date: "",
  });
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expData, typesData] = await Promise.all([
        expensesService.getExpenses(),
        expensesService.getExpenseTypes(),
      ]);
      setExpenses(expData);
      setExpenseTypes(typesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.expense_type_id ||
      !formData.amount ||
      !formData.expense_date
    ) {
      setModalState({
        isOpen: true,
        title: "خطا",
        message: "لطفا تمام مقادیر را وارد کنید.",
        type: "error",
      });
      return;
    }

    try {
      await expensesService.createExpense({
        expense_type_id: Number(formData.expense_type_id),
        amount: Number(formData.amount),
        expense_date: formData.expense_date,
      });

      setFormData({ expense_type_id: "", amount: "", expense_date: "" });
      setIsAddModalOpen(false);
      fetchData();

      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message: "هزینه با موفقیت ثبت شد.",
        type: "success",
      });
    } catch (error: any) {
      setModalState({
        isOpen: true,
        title: "خطا در ثبت اطلاعات",
        message: error.response?.data?.message || "خطای ناشناخته",
        type: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
            مدیریت هزینه‌ها
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            مشاهده و ثبت هزینه‌های صندوق
          </p>
        </div>
        {isAdmin ? (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 bg-persian-600 hover:bg-persian-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            ثبت هزینه جدید
          </button>
        ) : (
          <span className="px-3.5 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
            حالت فقط مشاهده (سرمایه‌گذار)
          </span>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400">
                <th className="p-4">ردیف</th>
                <th className="p-4">نوع هزینه</th>
                <th className="p-4">مبلغ (ریال)</th>
                <th className="p-4">تاریخ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
              {expenses.length > 0 ? (
                expenses.map((exp) => (
                  <tr
                    key={exp.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {toPersianNumbers(exp.id)}
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300">
                      {exp.expense_type_name}
                    </td>
                    <td className="p-4 font-bold text-persian-600 dark:text-persian-400">
                      {formatCurrency(exp.amount)}
                    </td>
                    <td className="p-4" dir="ltr">
                      {toPersianNumbers(exp.expense_date)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    هیچ هزینه‌ای ثبت نشده است.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="ثبت هزینه جدید"
        type="custom"
      >
        <form onSubmit={handleAddExpense} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              نوع هزینه
            </label>
            <select
              value={formData.expense_type_id}
              onChange={(e) =>
                setFormData({ ...formData, expense_type_id: e.target.value })
              }
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white"
            >
              <option value="">انتخاب کنید</option>
              {expenseTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              مبلغ (ریال)
            </label>
            <input
              type="text"
              dir="ltr"
              value={
                formData.amount
                  ? toPersianNumbers(
                      Number(formData.amount).toLocaleString("en-US"),
                    )
                  : ""
              }
              onChange={(e) => {
                const val = e.target.value;
                const enNumber = toEnglishNumbers(
                  val.replace(/,/g, "").replace(/،/g, ""),
                );
                if (/^\d*$/.test(enNumber)) {
                  setFormData({ ...formData, amount: enNumber });
                }
              }}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
              placeholder="۵,۰۰۰,۰۰۰"
            />
          </div>

          <div>
            <JalaliDatePicker
              label="تاریخ"
              value={formData.expense_date}
              onChange={(date) =>
                setFormData({ ...formData, expense_date: date })
              }
              placeholder="انتخاب تاریخ"
            />
          </div>

          <div className="pt-4 flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-persian-600 hover:bg-persian-700 text-white py-2 rounded-xl text-sm font-medium transition-colors"
            >
              ثبت هزینه
            </button>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        title=""
        type="custom"
      >
        <div className="text-center py-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              modalState.type === "success"
                ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {modalState.type === "success" ? (
              <Check className="w-8 h-8" />
            ) : (
              <AlertCircle className="w-8 h-8" />
            )}
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
            {modalState.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {modalState.message}
          </p>
          <button
            onClick={() =>
              setModalState((prev) => ({ ...prev, isOpen: false }))
            }
            className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            متوجه شدم
          </button>
        </div>
      </Modal>
    </div>
  );
}
